// src/modules/cad/cad-renderer.js

import L from "leaflet";


/**
 * ============================================================
 * CAD Renderer
 * MiningSurveyWebGIS
 * ============================================================
 */
const CAD_HIT_RADIUS_PX = 10;
const CADRenderer = {

    /**
     * ========================================================
     * Render all CAD layer results
     * ========================================================
     */

    render(layerResults) {

        /**
         * IMPORTANT:
         *
         * Gunakan featureGroup karena
         * CAD Import membutuhkan getBounds().
         */

        const group =
            L.featureGroup();

        group._cadVertexIndex = [];

        group._cadElevationLabels =
            L.layerGroup();

        group._cadElevationCallouts =
            L.layerGroup();


        for (
            const layerData of layerResults
        ) {

            const layer =
                this.renderLayer(
                    layerData
                );


            if (layer) {

                group.addLayer(
                    layer
                );

                if (
                    Array.isArray(
                        layer._cadVertexIndex
                    )
                ) {

                    group._cadVertexIndex.push(
                        ...layer._cadVertexIndex
                    );

                }
                if (
                    layer._cadElevationLabels
                ) {

                    for (
                        const label
                        of layer._cadElevationLabels.getLayers()
                    ) {

                        group._cadElevationLabels.addLayer(
                            label
                        );

                    }

                }

                if (
                    layer._cadElevationCallouts
                ) {

                    for (
                        const callout
                        of layer._cadElevationCallouts.getLayers()
                    ) {

                        group._cadElevationCallouts.addLayer(
                            callout
                        );

                    }
                }
            }

        }


        return group;
    },


    /**
     * ========================================================
     * Render one CAD layer
     * ========================================================
     */

    renderLayer(layerData) {

        if (
            !layerData ||
            !Array.isArray(
                layerData.rows
            )
        ) {

            console.warn(
                "[CAD Renderer] Invalid layer data"
            );

            return null;
        }


        const features = [];


        /**
         * ----------------------------------------------------
         * Convert rows → GeoJSON features
         * ----------------------------------------------------
         */

        for (
            const row of layerData.rows
        ) {
            const geometry =
                parseGeometry(
                    row.__geometry_json
                );

            if (!geometry) {
                continue;
            }


            const properties =
                createProperties(
                    row
                );


            features.push({

                type: "Feature",

                geometry,

                properties

            });

        }

        // ============================================================
        // DXF VERTEX FEATURES
        // ============================================================

        const vertices =
            Array.isArray(layerData.vertices)
                ? layerData.vertices
                : [];

        const vertexIndex = [];

        const vertexElevationLabels =
            L.layerGroup();
        
        const vertexElevationCallouts =
            L.layerGroup();

     

        console.log(
            "[CAD Renderer] Vertex data retained:",
            vertices.length
        );


        for (const vertex of vertices) {

            // cad-reader.js sudah menghasilkan geometry langsung
            const geometry =
                vertex.geometry;

            if (
                !geometry ||
                geometry.type !== "Point"
            ) {
                continue;
            }

            const coordinates =
                geometry.coordinates;

            if (
                !Array.isArray(coordinates) ||
                coordinates.length < 2
            ) {
                continue;
            }

            const vertexLatLng =
                L.latLng(
                    coordinates[1],
                    coordinates[0]
                );

            const z =
                Number(vertex.z);

            const elevation =
                Number.isFinite(z)
                    ? z
                    : null;

            // ========================================================
            // VERTEX ELEVATION LABEL
            // ========================================================

                if (elevation !== null) {

                    const calloutLength = 3;

                    const calloutEnd =
                        L.latLng(
                            vertexLatLng.lat,
                            vertexLatLng.lng +
                                (
                                    calloutLength /
                                    (
                                        111320 *
                                        Math.cos(
                                            vertexLatLng.lat *
                                            Math.PI /
                                            180
                                        )
                                    )
                                )
                        );

                    const calloutLine =
                        L.polyline(
                            [
                                vertexLatLng,
                                calloutEnd
                            ],
                            {
                                color: "#f2eceb",
                                weight: 1,
                                opacity: 0.8,
                                interactive: false
                            }
                        );

                    vertexElevationCallouts.addLayer(
                        calloutLine
                    );

                    const labelMarker =
                        L.marker(
                            calloutEnd,
                            {
                                icon:
                                    L.divIcon({
                                        className:
                                            "cad-vertex-elevation-marker",

                                        html:
                                            `<span class="cad-vertex-elevation-label">
                                                ${formatElevation(elevation)}
                                            </span>`,

                                        iconSize: null,

                                        iconAnchor: [0, 0]
                                    }),

                                interactive: false,

                                keyboard: false
                            }
                        );
                    // Hubungkan label dengan callout
                    labelMarker._cadCallout =
                        calloutLine;

                    labelMarker._cadLatLng =
                        vertexLatLng;

                    vertexElevationLabels.addLayer(
                        labelMarker
                    );
            
            }
            const vertexData = {

                handle:
                    String(
                        vertex.handle ?? ""
                    ),

                latlng:
                    vertexLatLng,

                z:
                    elevation,

                x:
                    Number(vertex.x),

                y:
                    Number(vertex.y),

                layer:
                    layerData.layerName ?? "CAD"
            };



            // --------------------------------------------------------
            // INDEX UNTUK IDENTIFY
            // --------------------------------------------------------

            vertexIndex.push(
                vertexData
            );

            // --------------------------------------------------------
            // FEATURE UNTUK DISPLAY
            /* --------------------------------------------------------

            features.push({

                type: "Feature",

                geometry,

                properties: {

                    Layer:
                        layerData.layerName ??
                        "CAD",

                    EntityHandle:
                        String(
                            vertex.handle ?? ""
                        ),

                    cadIsVertex:
                        true,

                    cadElevation:
                        elevation,

                    cadColor:
                        "#FF3D00",

                    cadColorSource:
                        "vertex-marker"

                }

            });
            */
        }

        console.log(
            "[CAD Renderer] Vertex index created:",
            vertexIndex.length
        );

        console.log(
            "[CAD Renderer] Total features after vertices:",
            features.length
        );
    
        /*
         * ----------------------------------------------------
         * Color diagnostic
         * ----------------------------------------------------
         */

        console.log(
            "[CAD Renderer] Color sample:"
        );


        console.table(
            features
                .slice(0, 20)
                .map(feature => ({

                    handle:
                        feature.properties?.EntityHandle,

                    layer:
                        feature.properties?.Layer,

                    color:
                        feature.properties?.cadColor,

                    source:
                        feature.properties?.cadColorSource,

                    aci:
                        feature.properties?.cadACI,

                    trueColor:
                        feature.properties?.cadTrueColor

                }))
        );


        if (
            !features.length
        ) {

            return null;

        }


        /**
         * ----------------------------------------------------
         * GeoJSON FeatureCollection
         * ----------------------------------------------------
         */

        const geojson = {

            type:
                "FeatureCollection",

            features

        };


        /**
         * ----------------------------------------------------
         * Leaflet layer
         * ----------------------------------------------------
         */

        const layer =
            L.geoJSON(
                geojson,
                {

                    /**
                     * ------------------------------------------------
                     * Line / Polygon
                     * ------------------------------------------------
                     */

                    style:
                        feature =>
                            getStyle(
                                feature
                            ),


                    /**
                     * ------------------------------------------------
                     * Point
                     * ------------------------------------------------
                     */

                    pointToLayer:
                        (
                            feature,
                            latlng
                        ) =>
                            createPoint(
                                feature,
                                latlng
                            ),


                    /**
                     * ------------------------------------------------
                     * Popup
                     * ------------------------------------------------
                     */

                    onEachFeature:
                        (
                            feature,
                            leafletLayer
                        ) => {

                            bindPopup(
                                feature,
                                leafletLayer
                            );

                            // Entity elevation
                            //bindElevationLabel(
                              //  feature,
                              //  leafletLayer
                            //);

                        }

                }
            );
            
            // ============================================================
            // CAD VERTEX INDEX
            // ============================================================

            layer._cadVertexIndex =
                vertexIndex;

            layer._cadElevationLabels =
                vertexElevationLabels;

            layer._cadElevationCallouts =
                vertexElevationCallouts;

            return layer;
        },
};

function createCADHitLayer(feature) {

    const geometryType =
        feature.geometry?.type;

    // Hanya untuk garis
    if (
        geometryType !== "LineString" &&
        geometryType !== "MultiLineString"
    ) {
        return null;
    }

    return L.geoJSON(
        feature,
        {
            style: {
                color: "#000000",
                weight: CAD_HIT_RADIUS_PX * 2,
                opacity: 0,
                fillOpacity: 0,
                interactive: true
            }
        }
    );
}

/**
 * ============================================================
 * Parse GeoJSON
 * ============================================================
 */

function parseGeometry(value) {

    if (!value) {

        return null;

    }


    try {

        if (
            typeof value === "string"
        ) {

            return JSON.parse(
                value
            );

        }


        return value;

    } catch (error) {

        console.warn(
            "[CAD Renderer] Invalid geometry:",
            value
        );

        return null;

    }
}


/**
 * ============================================================
 * CAD Properties
 * ============================================================
 */

function createProperties(row) {

    const properties = {};


    /**
     * --------------------------------------------------------
     * CAD Layer
     * --------------------------------------------------------
     */

    if (
        row.Layer !== null &&
        row.Layer !== undefined
    ) {

        properties.Layer =
            row.Layer;

    }


    /**
     * --------------------------------------------------------
     * Entity Handle
     * --------------------------------------------------------
     */

    if (
        row.EntityHandle !== null &&
        row.EntityHandle !== undefined
    ) {

        properties.EntityHandle =
            String(
                row.EntityHandle
            );

    }


    /**
     * --------------------------------------------------------
     * Text
     * --------------------------------------------------------
     */

    if (
        row.Text !== null &&
        row.Text !== undefined &&
        String(
            row.Text
        ).trim() !== ""
    ) {

        properties.Text =
            row.Text;

    }


    /**
     * --------------------------------------------------------
     * PaperSpace
     * --------------------------------------------------------
     */

    if (
        row.PaperSpace !== null &&
        row.PaperSpace !== undefined
    ) {

        properties.PaperSpace =
            row.PaperSpace;

    }


    /**
     * --------------------------------------------------------
     * SubClasses
     * --------------------------------------------------------
     */

    if (
        row.SubClasses !== null &&
        row.SubClasses !== undefined
    ) {

        properties.SubClasses =
            row.SubClasses;

    }


    /**
     * --------------------------------------------------------
     * Linetype
     * --------------------------------------------------------
     */

    if (
        row.Linetype !== null &&
        row.Linetype !== undefined
    ) {

        properties.Linetype =
            row.Linetype;

    }


    /**
     * ========================================================
     * DXF COLOR
     * ========================================================
     *
     * Prioritas:
     *
     * 1. row.__cad_color
     * 2. row.cadColor
     * 3. True Color
     * 4. ACI
     * 5. default
     *
     * ========================================================
     */

    const colorInfo =
        resolveColor(
            row
        );


    properties.cadColor =
        colorInfo.color;


    properties.cadColorSource =
        colorInfo.source;


    properties.cadACI =
        row.__cad_aci ??
        row.cadACI ??
        colorInfo.aci ??
        null;


    properties.cadTrueColor =
        row.__cad_true_color ??
        row.cadTrueColor ??
        colorInfo.trueColor ??
        null;


    properties.cadLayerColor =
        row.__cad_layer_color ??
        row.cadLayerColor ??
        null;
    
    /**
 * --------------------------------------------------------
 * Original DXF Elevation
 * --------------------------------------------------------
 */

    if (
        row.__cad_elevation !== null &&
        row.__cad_elevation !== undefined &&
        Number.isFinite(
            Number(
                row.__cad_elevation
            )
        )
    ) {

        properties.cadElevation =
            Number(
                row.__cad_elevation
            );

    }

    /*
 * ========================================================
 * CAD VERTEX
 * ========================================================
 */

properties.cadIsVertex =
    row.__cad_is_vertex === true;

    return properties;
}

/**
 * ============================================================
 * Resolve CAD color
 * ============================================================
 *
 * Priority:
 *
 * 1. True Color yang valid
 * 2. ACI
 * 3. cadColor yang valid
 * 4. default
 *
 * Jangan menerima #000000 dari parser sebagai
 * true-color apabila trueColor sebenarnya null.
 * ============================================================
 */

function resolveColor(row) {

    /**
     * --------------------------------------------------------
     * 1. TRUE COLOR
     * --------------------------------------------------------
     */

    const trueColorValue =
        row.__cad_true_color ??
        row.cadTrueColor ??
        null;


    const trueColor =
        trueColorToHex(
            trueColorValue
        );


    if (trueColor) {

        return {

            color:
                trueColor,

            source:
                "entity-true-color",

            aci:
                row.__cad_aci ??
                row.cadACI ??
                null,

            trueColor:
                trueColorValue

        };

    }


    /**
     * --------------------------------------------------------
     * 2. ACI
     * --------------------------------------------------------
     *
     * Ini yang sekarang terlewat.
     *
     * Contoh:
     *
     * ACI 1 → merah
     * ACI 4 → cyan
     * ACI 7 → putih
     */

    const aci =
        normalizeACI(
            row.__cad_aci ??
            row.cadACI
        );


    if (
        aci !== null &&
        aci !== 0 &&
        aci !== 256 &&
        aci !== 257
    ) {

        return {

            color:
                aciToHex(
                    aci
                ),

            source:
                "entity-aci",

            aci,

            trueColor:
                null

        };

    }


    /**
     * --------------------------------------------------------
     * 3. Direct CAD color
     * --------------------------------------------------------
     */

    const directColor =
        normalizeColor(
            row.__cad_color
        );


    if (directColor) {

        return {

            color:
                directColor,

            source:
                row.__cad_color_source ??
                "entity",

            aci:
                aci,

            trueColor:
                trueColorValue

        };

    }


    /**
     * --------------------------------------------------------
     * 4. Layer color
     * --------------------------------------------------------
     */

    const layerColor =
        normalizeColor(
            row.__cad_layer_color
        );


    if (layerColor) {

        return {

            color:
                layerColor,

            source:
                "layer",

            aci,

            trueColor:
                trueColorValue

        };

    }


    /**
     * --------------------------------------------------------
     * 5. Default
     * --------------------------------------------------------
     */

    return {

        color:
            "#222222",

        source:
            "default",

        aci:
            aci,

        trueColor:
            trueColorValue

    };
}


/**
 * ============================================================
 * Line / Polygon style
 * ============================================================
 */

function getStyle(feature) {

    const geometryType =
        feature.geometry?.type;


    const color =
        resolveFeatureColor(
            feature
        );


    /**
     * --------------------------------------------------------
     * Polygon
     * --------------------------------------------------------
     */

    if (
        geometryType === "Polygon" ||
        geometryType === "MultiPolygon"
    ) {

        return {

            color,

            weight:
                4,

            opacity:
                1,

            fillColor:
                color,

            fillOpacity:
                0.15

        };

    }


    /**
     * --------------------------------------------------------
     * LineString / MultiLineString
     * --------------------------------------------------------
     */

    return {

        color,

        weight:
            4,

        opacity:
            1,

        fillOpacity:
            0

    };
}


/**
 * ============================================================
 * Resolve feature color
 * ============================================================
 */

function resolveFeatureColor(feature) {

    const properties =
        feature.properties ||
        {};


    /**
     * --------------------------------------------------------
     * 1. True Color
     * --------------------------------------------------------
     */

    const trueColor =
        trueColorToHex(
            properties.cadTrueColor
        );


    if (trueColor) {

        return trueColor;

    }


    /**
     * --------------------------------------------------------
     * 2. ACI
     * --------------------------------------------------------
     */

    const aci =
        normalizeACI(
            properties.cadACI
        );


    if (
        aci !== null &&
        aci !== 0 &&
        aci !== 256 &&
        aci !== 257
    ) {

        return aciToHex(
            aci
        );

    }


    /**
     * --------------------------------------------------------
     * 3. cadColor
     * --------------------------------------------------------
     */

    const color =
        normalizeColor(
            properties.cadColor
        );


    if (color) {

        return color;

    }


    return "#222222";
}


/**
 * ============================================================
 * Point
 * ============================================================
 */
function createPoint(
    feature,
    latlng
) {

    const properties =
        feature.properties || {};

    const color =
        resolveFeatureColor(
            feature
        );

    /*
     * ========================================================
     * DXF VERTEX
     * ========================================================
     *
     * Gunakan crosshair yang lebih jelas
     * daripada circle kecil.
     */
   if (
    properties.cadIsVertex === true
) {

    const icon =
        L.divIcon({

            className:
                "cad-vertex-icon",

            html: `
                <div class="cad-vertex-hit">
                    <div class="cad-vertex-symbol">
                        <div class="cad-vertex-cross horizontal"></div>
                        <div class="cad-vertex-cross vertical"></div>
                        <div class="cad-vertex-center"></div>
                    </div>
                </div>
            `,

            iconSize: [
                16,
                16
            ],

            iconAnchor: [
                13,
                13
            ]
        });

    return L.marker(
        latlng,
        {
            icon,

            zIndexOffset:
                10000,

            bubblingMouseEvents:
                true,

            interactive:
                true
        }
    );

    }

    /*
     * ========================================================
     * CAD Point biasa
     * ========================================================
     */

    return L.circleMarker(
        latlng,
        {
            radius: 2,

            color,

            weight: 2,

            opacity: 1,

            fillColor: color,

            fillOpacity: 1
        }
    );
}

/**
 * ============================================================
 * Popup - Elevation Only
 * ============================================================
 */

   function bindPopup(
    feature,
    leafletLayer
) {

    const properties =
        feature.properties || {};

    const z =
        Number(
            properties.cadElevation
        );

    /*
     * ========================================================
     * VERTEX
     * ========================================================
     */

    if (
        properties.cadIsVertex === true
    ) {

        if (
            Number.isFinite(z)
        ) {

            leafletLayer.bindPopup(`
                <div class="cad-popup">

                    <div style="
                        font-size:10px;
                        color:#777;
                        margin-bottom:3px;
                    ">
                        RL :
                    </div>

                    <div style="
                        font-size:12px;
                        font-weight:600;
                    ">
                        ${formatElevation(z)} m
                    </div>

                </div>
            `, {
                className: "cad-popup-wrapper"
            });

        } else {

            leafletLayer.bindPopup(`
                <div class="cad-popup">

                    <div style="
                        font-size:12px;
                        color:#777;
                        margin-top:4px;
                    ">
                        RL : -
                    </div>

                </div>
            `, {
                className: "cad-popup-wrapper"
            });
        }

        return;
    }

    /*
     * ========================================================
     * Object CAD biasa
     * ========================================================
     */

    if (
        !Number.isFinite(z)
    ) {

        leafletLayer.bindPopup(`
            <div class="cad-popup">

                <div style="
                    font-size:10px;
                    color:#777;
                ">
                    Elevasi :
                </div>

                <div style="
                    font-size:12px;
                    font-weight:500;
                ">
                    -
                </div>

            </div>
        `, {
            className: "cad-popup-wrapper"
        });

        return;
    }

    leafletLayer.bindPopup(`
        <div class="cad-popup">

            <div style="
                font-size:10px;
                color:#777;
                margin-bottom:3px;
            ">
                Elevasi :
            </div>

            <div style="
                font-size:12px;
                font-weight:500;
            ">
                ${formatElevation(z)} m
            </div>

        </div>
    `, {
        className: "cad-popup-wrapper"
    }); 
}

/**
 * ============================================================
 * Elevation Label
 * ============================================================
 */

function bindElevationLabel(
    feature,
    leafletLayer
) {

    const properties =
        feature.properties || {};

    const z =
        Number(
            properties.cadElevation
        );

    // Tidak ada elevasi
    if (
        !Number.isFinite(z)
    ) {

        return;

    }

    leafletLayer.bindTooltip(
        formatElevation(z),
        {
            permanent: true,

            direction: "center",

            className:
                "elevation-label",

            offset: [0, 0]
        }
    );
}

/**
 * ============================================================
 * CAD Vertex Elevation Label
 * ============================================================
 */

function bindVertexElevationLabel(
    feature,
    leafletLayer
) {

    const z =
        Number(
            feature.properties?.cadElevation
        );

    if (
        !Number.isFinite(z)
    ) {
        return;
    }

    if (
        !leafletLayer ||
        typeof leafletLayer.bindTooltip !==
            "function"
    ) {
        return;
    }

    leafletLayer.bindTooltip(
        formatElevation(z),
        {
            permanent: true,
            direction: "right",
            className:
                "cad-vertex-elevation-label",
            offset: [5, 0]
        }
    );
}


//=================================
function formatElevation(
    value
) {

    return Number(
        value
    ).toFixed(
        1
    );
}


/**
 * ============================================================
 * Normalize HEX color
 * ============================================================
 */

function normalizeColor(
    value
) {

    if (
        typeof value !== "string"
    ) {

        return null;

    }


    const color =
        value.trim();


    /**
     * #RRGGBB
     */

    if (
        /^#[0-9A-Fa-f]{6}$/.test(
            color
        )
    ) {

        return color;

    }


    /**
     * #RGB
     */

    if (
        /^#[0-9A-Fa-f]{3}$/.test(
            color
        )
    ) {

        return (

            "#" +

            color[1] +
            color[1] +

            color[2] +
            color[2] +

            color[3] +
            color[3]

        );

    }


    /**
     * rgb(...)
     */

    const rgbMatch =
        color.match(
            /^rgb\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)$/i
        );


    if (rgbMatch) {

        const r =
            Number(
                rgbMatch[1]
            );

        const g =
            Number(
                rgbMatch[2]
            );

        const b =
            Number(
                rgbMatch[3]
            );


        if (
            r <= 255 &&
            g <= 255 &&
            b <= 255
        ) {

            return rgbToHex(
                r,
                g,
                b
            );

        }

    }


    return null;
}


/**
 * ============================================================
 * Normalize ACI
 * ============================================================
 */

function normalizeACI(
    value
) {

    if (
        value === null ||
        value === undefined ||
        value === ""
    ) {

        return null;

    }


    const number =
        Number(
            value
        );


    if (
        !Number.isFinite(
            number
        )
    ) {

        return null;

    }


    return Math.trunc(
        number
    );
}


/**
 * ============================================================
 * AutoCAD ACI → HEX
 *
 * Standard AutoCAD indexed colors.
 * ============================================================
 */

function aciToHex(
    aci
) {

    const colors = {

        0:
            "#000000",

        1:
            "#FF0000",

        2:
            "#FFFF00",

        3:
            "#00FF00",

        4:
            "#00FFFF",

        5:
            "#0000FF",

        6:
            "#FF00FF",

        7:
            "#FFFFFF",

        8:
            "#808080",

        9:
            "#C0C0C0",

        10:
            "#FF0000",

        11:
            "#FF7F7F",

        12:
            "#CC0000",

        13:
            "#CC6666",

        14:
            "#990000",

        15:
            "#993333",

        16:
            "#7F0000",

        17:
            "#7F3F3F",

        18:
            "#4C0000",

        19:
            "#4C2626",

        20:
            "#FF3F00",

        21:
            "#FF9F7F",

        22:
            "#CC3300",

        23:
            "#CC7F66",

        24:
            "#992600",

        25:
            "#997766",

        26:
            "#7F1F00",

        27:
            "#7F4F3F",

        28:
            "#4C1300",

        29:
            "#4C2F26",

        30:
            "#FF7F00",

        31:
            "#FFBF7F",

        32:
            "#CC6600",

        33:
            "#CC9966",

        34:
            "#994C00",

        35:
            "#997F66",

        36:
            "#7F3F00",

        37:
            "#7F5F3F",

        38:
            "#4C2600",

        39:
            "#4C3926",

        40:
            "#FFBF00",

        41:
            "#FFDF7F",

        42:
            "#CC9900",

        43:
            "#CCB266",

        44:
            "#997300",

        45:
            "#998566",

        46:
            "#7F5F00",

        47:
            "#7F6F3F",

        48:
            "#4C3900",

        49:
            "#4C4326",

        50:
            "#FFFF00",

        51:
            "#FFFF7F",

        52:
            "#CCCC00",

        53:
            "#CCCC66",

        54:
            "#999900",

        55:
            "#999966",

        56:
            "#7F7F00",

        57:
            "#7F7F3F",

        58:
            "#4C4C00",

        59:
            "#4C4C26",

        60:
            "#BFFF00",

        61:
            "#DFFF7F",

        62:
            "#99CC00",

        63:
            "#B2CC66",

        64:
            "#739900",

        65:
            "#859966",

        66:
            "#5F7F00",

        67:
            "#6F7F3F",

        68:
            "#394C00",

        69:
            "#434C26",

        70:
            "#7FFF00",

        71:
            "#BFFF7F",

        72:
            "#66CC00",

        73:
            "#99CC66",

        74:
            "#4C9900",

        75:
            "#7F9966",

        76:
            "#3F7F00",

        77:
            "#5F7F3F",

        78:
            "#264C00",

        79:
            "#394C26",

        80:
            "#3FFF00",

        81:
            "#9FFF7F",

        82:
            "#33CC00",

        83:
            "#66CC66",

        84:
            "#269900",

        85:
            "#669966",

        86:
            "#1F7F00",

        87:
            "#4F7F3F",

        88:
            "#134C00",

        89:
            "#324C26",

        90:
            "#00FF00",

        91:
            "#7FFF7F",

        92:
            "#00CC00",

        93:
            "#66CC66",

        94:
            "#009900",

        95:
            "#669966",

        96:
            "#007F00",

        97:
            "#3F7F3F",

        98:
            "#004C00",

        99:
            "#264C26",

        100:
            "#00FF3F",

        101:
            "#7FFFBF",

        102:
            "#00CC33",

        103:
            "#66CC99",

        104:
            "#009926",

        105:
            "#66997F",

        106:
            "#007F1F",

        107:
            "#3F7F5F",

        108:
            "#004C13",

        109:
            "#264C39",

        110:
            "#00FF7F",

        111:
            "#7FFFBF",

        112:
            "#00CC66",

        113:
            "#66CC99",

        114:
            "#00994C",

        115:
            "#66997F",

        116:
            "#007F3F",

        117:
            "#3F7F5F",

        118:
            "#004C26",

        119:
            "#264C39",

        120:
            "#00FFBF",

        121:
            "#7FFFFF",

        122:
            "#00CC99",

        123:
            "#66CCCC",

        124:
            "#009973",

        125:
            "#669999",

        126:
            "#007F5F",

        127:
            "#3F7F7F",

        128:
            "#004C39",

        129:
            "#264C4C",

        130:
            "#00FFFF",

        131:
            "#7FFFFF",

        132:
            "#00CCCC",

        133:
            "#66CCCC",

        134:
            "#009999",

        135:
            "#669999",

        136:
            "#007F7F",

        137:
            "#3F7F7F",

        138:
            "#004C4C",

        139:
            "#264C4C",

        140:
            "#00BFFF",

        141:
            "#7FDFFF",

        142:
            "#0099CC",

        143:
            "#66B2CC",

        144:
            "#007399",

        145:
            "#668599",

        146:
            "#005F7F",

        147:
            "#3F6F7F",

        148:
            "#00394C",

        149:
            "#26434C",

        150:
            "#007FFF",

        151:
            "#7FBFFF",

        152:
            "#0066CC",

        153:
            "#6699CC",

        154:
            "#004C99",

        155:
            "#667F99",

        156:
            "#003F7F",

        157:
            "#3F5F7F",

        158:
            "#00264C",

        159:
            "#26394C",

        160:
            "#003FFF",

        161:
            "#7F9FFF",

        162:
            "#0033CC",

        163:
            "#667FCC",

        164:
            "#002699",

        165:
            "#666699",

        166:
            "#001F7F",

        167:
            "#3F4F7F",

        168:
            "#00134C",

        169:
            "#26324C",

        170:
            "#0000FF",

        171:
            "#7F7FFF",

        172:
            "#0000CC",

        173:
            "#6666CC",

        174:
            "#000099",

        175:
            "#666699",

        176:
            "#00007F",

        177:
            "#3F3F7F",

        178:
            "#00004C",

        179:
            "#26264C",

        180:
            "#3F00FF",

        181:
            "#9F7FFF",

        182:
            "#3300CC",

        183:
            "#7F66CC",

        184:
            "#260099",

        185:
            "#776699",

        186:
            "#1F007F",

        187:
            "#4F3F7F",

        188:
            "#13004C",

        189:
            "#32264C",

        190:
            "#7F00FF",

        191:
            "#BF7FFF",

        192:
            "#6600CC",

        193:
            "#9966CC",

        194:
            "#4C0099",

        195:
            "#7F6699",

        196:
            "#3F007F",

        197:
            "#5F3F7F",

        198:
            "#26004C",

        199:
            "#39264C",

        200:
            "#BF00FF",

        201:
            "#DF7FFF",

        202:
            "#9900CC",

        203:
            "#B266CC",

        204:
            "#730099",

        205:
            "#856699",

        206:
            "#5F007F",

        207:
            "#6F3F7F",

        208:
            "#39004C",

        209:
            "#43264C",

        210:
            "#FF00FF",

        211:
            "#FF7FFF",

        212:
            "#CC00CC",

        213:
            "#CC66CC",

        214:
            "#990099",

        215:
            "#996699",

        216:
            "#7F007F",

        217:
            "#7F3F7F",

        218:
            "#4C004C",

        219:
            "#4C264C",

        220:
            "#FF00BF",

        221:
            "#FF7FDF",

        222:
            "#CC0099",

        223:
            "#CC66B2",

        224:
            "#990073",

        225:
            "#996685",

        226:
            "#7F005F",

        227:
            "#7F3F6F",

        228:
            "#4C0039",

        229:
            "#4C2643",

        230:
            "#FF007F",

        231:
            "#FF7FBF",

        232:
            "#CC0066",

        233:
            "#CC6699",

        234:
            "#99004C",

        235:
            "#99667F",

        236:
            "#7F003F",

        237:
            "#7F3F5F",

        238:
            "#4C0026",

        239:
            "#4C2639",

        240:
            "#FF003F",

        241:
            "#FF9F7F",

        242:
            "#CC0033",

        243:
            "#CC6666",

        244:
            "#990026",

        245:
            "#996666",

        246:
            "#7F001F",

        247:
            "#7F3F4F",

        248:
            "#4C0013",

        249:
            "#4C2632",

        250:
            "#FF007F",

        251:
            "#FF7FBF",

        252:
            "#CC0066",

        253:
            "#CC6699",

        254:
            "#99004C",

        255:
            "#99667F"

    };


    /**
     * True Color
     */

    if (
        colors[
            aci
        ]
    ) {

        return colors[
            aci
        ];

    }


    /**
     * Gray / special colors
     */

    if (
        aci === 256
    ) {

        return "#222222";

    }


    if (
        aci === 257
    ) {

        return "#222222";

    }


    return "#222222";
}


/**
 * ============================================================
 * True Color → HEX
 *
 * AutoCAD true color is normally:
 *
 * 0x00RRGGBB
 * ============================================================
 */

function trueColorToHex(
    value
) {

    if (
        value === null ||
        value === undefined ||
        value === ""
    ) {

        return null;

    }


    /**
     * Already HEX
     */

    const normalized =
        normalizeColor(
            String(
                value
            )
        );


    if (normalized) {

        return normalized;

    }


    /**
     * Numeric true color
     */

    let number;


    if (
        typeof value === "bigint"
    ) {

        number =
            Number(
                value
            );

    } else {

        number =
            Number(
                value
            );

    }


    if (
        !Number.isFinite(
            number
        )
    ) {

        return null;

    }


    number =
        Math.trunc(
            number
        );


    /**
     * AutoCAD true color:
     *
     * 0x00RRGGBB
     */

    const r =
        (number >> 16) &
        0xff;


    const g =
        (number >> 8) &
        0xff;


    const b =
        number &
        0xff;


    return rgbToHex(
        r,
        g,
        b
    );
}


/**
 * ============================================================
 * RGB → HEX
 * ============================================================
 */

function rgbToHex(
    r,
    g,
    b
) {

    return (

        "#" +

        toHex(r) +

        toHex(g) +

        toHex(b)

    );
}


function toHex(
    value
) {

    return Math.max(
        0,
        Math.min(
            255,
            Math.round(
                value
            )
        )
    )
        .toString(16)
        .padStart(
            2,
            "0"
        )
        .toUpperCase();
}




/**
 * ============================================================
 * Escape HTML
 * ============================================================
 */

function escapeHTML(
    value
) {

    return String(
        value
    )
        .replace(
            /&/g,
            "&amp;"
        )
        .replace(
            /</g,
            "&lt;"
        )
        .replace(
            />/g,
            "&gt;"
        )
        .replace(
            /"/g,
            "&quot;"
        )
        .replace(
            /'/g,
            "&#039;"
        );
}


/**
 * ============================================================
 * Identify CAD Vertex by Click Radius
 * ============================================================
 */

export function findNearestCADVertex(
    cadLayer,
    clickLatLng,
    radiusMeters = 3
) {

    if (
        !cadLayer ||
        !clickLatLng
    ) {
        return null;
    }

    const vertices =
        cadLayer._cadVertexIndex;

    if (
        !Array.isArray(vertices) ||
        vertices.length === 0
    ) {
        return null;
    }

    let nearest = null;
    let nearestDistance = Infinity;

    for (
        const vertex of vertices
    ) {

        if (
            !vertex.latlng
        ) {
            continue;
        }

        const distance =
            clickLatLng.distanceTo(
                vertex.latlng
            );

        if (
            distance <= radiusMeters &&
            distance < nearestDistance
        ) {

            nearest = vertex;
            nearestDistance = distance;
        }
    }

    if (!nearest) {
        return null;
    }

    return {
        ...nearest,
        distance: nearestDistance
    };
}

export function showCADVertexPopup(
    map,
    vertex
) {

    if (
        !map ||
        !vertex ||
        !vertex.latlng
    ) {
        return;
    }

    const elevation =
        Number(vertex.z);

    const elevationText =
        Number.isFinite(elevation)
            ? `${elevation.toFixed(2)} m`
            : "-";

    const html = `
        <div class="cad-popup">

            <div style="
                font-size:12px;
                color:#777;
                margin-bottom:3px;
            ">
                RL :
            </div>

            <div style="
                font-size:13px;
                color:#777;
                margin-bottom:3px;
            ">
                Elevation
            </div>

            <div style="
                font-size:14px;
                font-weight:700;
            ">
                ${elevationText}
            </div>

        </div>
    `;

    L.popup({
        closeButton: true,
        autoClose: true,
        closeOnClick: true
    })
        .setLatLng(
            vertex.latlng
        )
        .setContent(
            html
        )
        .openOn(map);
}

export default CADRenderer;